import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaComments, FaTimes, FaPaperPlane, FaRobot, FaUser } from 'react-icons/fa';

const AiChat = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      text: t('aiChat.welcome'),
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Call Gemini AI
      const aiResponse = await callGeminiAI(inputValue);
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: aiResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: t('aiChat.error'),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const callGeminiAI = async (userInput) => {
    try {
      // Get API key from environment variables or use a fallback
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY || 'YOUR_GEMINI_API_KEY';
      
      // Create the prompt for Ismail's AI assistant
      const systemPrompt = `You are Ismail's AI assistant. Ismail is a React and React Native developer from Morocco. He specializes in:
      - React and React Native development
      - JavaScript, Redux, Firebase
      - Full-stack web and mobile applications
      - Freelance services available
      - Based in Tangier, Morocco
      - Email: ismail_coder@protonmail.com
      - Available for hire
      
      Please answer questions about Ismail's skills, services, experience, and availability. Be helpful and professional. Keep responses concise and informative.`;

      const requestBody = {
        contents: [
          {
            parts: [
              { text: systemPrompt },
              { text: userInput }
            ]
          }
        ],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 1024,
        }
      };

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        return data.candidates[0].content.parts[0].text;
      } else {
        throw new Error('Invalid response format from Gemini API');
      }
    } catch (error) {
      console.error('Error calling Gemini API:', error);
      // Fallback to a helpful message
      return t('aiChat.error');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
          isOpen 
            ? 'bg-red-500 hover:bg-red-600' 
            : 'bg-blue-500 hover:bg-blue-600 hover:scale-110'
        }`}
        style={{backgroundColor: isOpen ? 'var(--destructive)' : 'var(--primary)'}}
      >
        {isOpen ? (
          <FaTimes className="text-white text-xl" />
        ) : (
          <FaComments className="text-white text-xl" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-40 w-80 sm:w-96 h-96 bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden"
             style={{backgroundColor: 'var(--card)', borderColor: 'var(--border)'}}>
          
          {/* Chat Header */}
          <div className="p-4 border-b flex items-center space-x-3"
               style={{backgroundColor: 'var(--muted)', borderColor: 'var(--border)'}}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center"
                 style={{backgroundColor: 'var(--primary)'}}>
              <FaRobot className="text-white text-sm" />
            </div>
            <div>
              <h3 className="font-semibold text-sm" style={{color: 'var(--foreground)'}}>
                {t('aiChat.title')}
              </h3>
              <p className="text-xs" style={{color: 'var(--muted-foreground)'}}>
                {t('aiChat.subtitle')}
              </p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.type === 'user' ? 'ml-2' : 'mr-2'
                  }`}
                       style={{backgroundColor: message.type === 'user' ? 'var(--primary)' : 'var(--accent)'}}>
                    {message.type === 'user' ? (
                      <FaUser className="text-white text-xs" />
                    ) : (
                      <FaRobot className="text-white text-xs" />
                    )}
                  </div>
                  <div
                    className={`rounded-lg px-3 py-2 ${
                      message.type === 'user'
                        ? 'text-white'
                        : ''
                    }`}
                    style={{
                      backgroundColor: message.type === 'user' ? 'var(--primary)' : 'var(--muted)',
                      color: message.type === 'user' ? 'var(--primary-foreground)' : 'var(--foreground)'
                    }}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                    <p className="text-xs mt-1 opacity-70">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center mr-2"
                       style={{backgroundColor: 'var(--accent)'}}>
                    <FaRobot className="text-white text-xs" />
                  </div>
                  <div className="rounded-lg px-3 py-2" style={{backgroundColor: 'var(--muted)'}}>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{backgroundColor: 'var(--muted-foreground)'}}></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{backgroundColor: 'var(--muted-foreground)'}}></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{backgroundColor: 'var(--muted-foreground)'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t" style={{borderColor: 'var(--border)'}}>
            <div className="flex space-x-2">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('aiChat.placeholder')}
                className="flex-1 resize-none rounded-lg px-3 py-2 text-sm outline-none transition duration-200"
                style={{
                  backgroundColor: 'var(--input)',
                  borderColor: 'var(--border)',
                  color: 'var(--foreground)'
                }}
                rows={1}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{backgroundColor: 'var(--primary)'}}
              >
                <FaPaperPlane className="text-white text-sm" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AiChat;